/**
 * Hook para cargar opciones de PreInvoices e Invoices con paginación e infinite scroll.
 * Usado en los selectores de filtro de InvoicesOrders.
 */

import { useCallback } from 'react';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { PreInvoicesApiAdapter } from '@modules/Invoices/PreInvoices/List/adapters/PreInvoicesApiAdapter';
import { InvoicesApiAdapter } from '@modules/Invoices/Invoices/List/adapters/InvoicesApiAdapter';
import type { MultiSelectOption, LoadOptionsResponse } from '@shared/components/ui/MultiSelect';

export const usePreInvoicesAndInvoicesLoadOptions = () => {
  const loadPreInvoicesOptions = useCallback(
    async (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> => {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new PreInvoicesApiAdapter(httpClient);
      const result = await adapter.getPreInvoicesPaginated({
        search: params.search || undefined,
        page: params.page,
        limit: params.limit,
      });
      const options: MultiSelectOption[] = result.preInvoices.map((p) => ({
        value: String(p.id),
        label: p.code || `PRE-${p.id}`,
        description: p.status,
      }));
      return {
        options,
        hasMore: result.hasMore,
      };
    },
    []
  );

  const loadInvoicesOptions = useCallback(
    async (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> => {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new InvoicesApiAdapter(httpClient);
      const result = await adapter.getInvoicesPaginated({
        search: params.search || undefined,
        page: params.page,
        limit: params.limit,
      });
      const options: MultiSelectOption[] = result.invoices.map((inv) => ({
        value: String(inv.id),
        label: inv.erp_number || inv.preinvoice?.code || `INV-${inv.id}`,
        description: inv.date ? new Date(inv.date).toLocaleDateString('es-ES') : undefined,
      }));
      return {
        options,
        hasMore: result.hasMore,
      };
    },
    []
  );

  return { loadPreInvoicesOptions, loadInvoicesOptions };
};
